// openEHR RM model — BMM package org.openehr.rm.composition
// Generated by tasks/split_model_by_bmm_packages.ts from tasks/bmm_package_map.json.
// Package boundaries follow the BMM package structure; edit the class bodies here,
// but re-run the splitter rather than hand-moving declarations between packages.

import { installAssignmentCoercion } from "../../base/apply_rm_init.ts";
import { initArchetyped, initCodePhrase, initDvCodedText, initDvText, initEventContext, initObjectVersionId, initPartyProxy } from "../../base/init_helpers.ts";
import type { CompositionInit } from "../_unassigned.ts";
import { LOCATABLE, PATHABLE } from "../common/archetyped.ts";
import type { PARTY_IDENTIFIED, PARTY_PROXY } from "../common/generic.ts";
import type { ITEM_STRUCTURE } from "../data_structures/item_structure.ts";
import type { DV_DATE_TIME } from "../data_types/quantity/date_time.ts";
import type { CODE_PHRASE, DV_CODED_TEXT } from "../data_types/text.ts";
import * as openehr_base from "../../base/mod.ts";
import { applyRmInit, type RmInit } from "../../base/apply_rm_init.ts";

export class COMPOSITION extends LOCATABLE {
  /**
   * Constructor supports partial initialization via spread syntax.
   * Accepts primitives, objects, and terse strings where appropriate.
   * 
   * @param init - Optional initialization object
   * 
   * @example
   * ```typescript
   * // Compact creation with terse format
   * const composition = new COMPOSITION({
   *   archetype_node_id: "openEHR-EHR-COMPOSITION.encounter.v1",
   *   name: "Blood Pressure Reading",
   *   language: "ISO_639-1::en",
   *   territory: "ISO_3166-1::GB",
   *   category: "openehr::433|event|",
   *   composer: { name: "Dr. Smith" }
   * });
   * ```
   */
  constructor(init?: Partial<CompositionInit>) {
    super();
    installAssignmentCoercion(this);
    if (init) {
      this._applyInit(init);
    }
  }
  
  /**
   * Internal helper to process initialization data.
   * @private
   */
  private _applyInit(init: Partial<CompositionInit>): void {
    // LOCATABLE properties
    if (init.archetype_node_id !== undefined) {
      this.archetype_node_id = init.archetype_node_id;
    }
    if (init.name !== undefined) {
      this.name = initDvText(init.name as any);
    }
    if (init.uid !== undefined) {
      this.uid = initObjectVersionId(init.uid as any);
    }
    if (init.archetype_details !== undefined) {
      this.archetype_details = initArchetyped(init.archetype_details as any);
    }
    if (init.feeder_audit !== undefined) {
      this.feeder_audit = init.feeder_audit as any;
    }
    if (init.links !== undefined) {
      this.links = init.links as any;
    }
    
    // COMPOSITION properties
    if (init.language !== undefined) {
      this.language = initCodePhrase(init.language as any);
    }
    if (init.territory !== undefined) {
      this.territory = initCodePhrase(init.territory as any);
    }
    if (init.category !== undefined) {
      this.category = initDvCodedText(init.category as any);
    }
    if (init.context !== undefined) {
      this.context = initEventContext(init.context as any);
    }
    if (init.composer !== undefined) {
      this.composer = initPartyProxy(init.composer as any);
    }
    if (init.content !== undefined) {
      this.content = init.content;
    }
  }

  /**
   * Mandatory indicator of the localised language in which this Composition is written. Coded from openEHR Code Set  \`languages\`. The language of an Entry if different from the Composition is indicated in \`ENTRY._language_\`.
   */
  language?: CODE_PHRASE;
  /**
   * Name of territory in which this Composition was written. Coded from openEHR  countries  code set, which is an expression of the ISO 3166 standard.
   */
  territory?: CODE_PHRASE;
  /**
   * Internal storage for category
   * @protected
   */
  protected _category?: DV_CODED_TEXT;

  /**
   * Temporal category of this Composition, i.e.
   *
   * * \`431|persistent|\` - of potential life-time validity;
   * * \`451|episodic|\` - valid over the life of a care episode;
   * * \`433|event|\` - valid at the time of recording (long-term validity requires subsequent clinical assessment).
   *
   * or any other code defined in the openEHR terminology group 'category'.
   */
  get category(): DV_CODED_TEXT | undefined {
    return this._category;
  }

  /**
   * Gets the DV_CODED_TEXT object for category.
   * Both category and $category return the same DV_CODED_TEXT instance.
   */
  get $category(): DV_CODED_TEXT | undefined {
    return this._category;
  }

  /**
   * Sets category from a DV_CODED_TEXT object.
   */
  set category(val: DV_CODED_TEXT | undefined) {
    this._category = val;
  }

  /**
   * The clinical session context of this Composition, i.e. the contextual attributes of the clinical session.
   */
  context?: EVENT_CONTEXT;
  /**
   * The person primarily responsible for the content of the Composition (but not necessarily its committal into the EHR system). This is the identifier which should appear on the screen. It may or may not be the person who entered the data. When it is the patient, the special self  instance of \`PARTY_PROXY\` will be used.
   */
  composer?: PARTY_PROXY;
  /**
   * The content of this Composition.
   */
  content?: undefined;
  /**
   * True if category is \`431|persistent|\`, False otherwise. Useful for finding Compositions in an EHR which are guaranteed to be of interest to most users.
   * @returns Result value
   */
  is_persistent(): openehr_base.Boolean {
    // Check if category code is "431" (persistent)
    if (!this.category || !this.category.defining_code) {
      return openehr_base.Boolean.from(false);
    }
    return openehr_base.Boolean.from(this.category.defining_code.code_string === "431");
  }
}

/**
 * Documents the context information of a healthcare event involving the subject of care and the health system. The context information recorded here are independent of the attributes recorded in the version audit, which document the  system interaction  context, i.e. the context of a user interacting with the health record system. Healthcare events include patient contacts, and any other business activity, such as pathology investigations which take place on behalf of the patient.
 */
export class EVENT_CONTEXT extends PATHABLE {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * Start time of the clinical session or other kind of event during which a provider performs a service of any kind for the patient.
   */
  start_time?: DV_DATE_TIME;
  /**
   * Optional end time of the clinical session.
   */
  end_time?: DV_DATE_TIME;
  /**
   * Internal storage for location
   * @protected
   */
  protected _location?: openehr_base.String;

  /**
   * The actual location where the session occurred, e.g. 'microbiology lab 2', 'home', 'ward A3'  and so on.
   */
  get location(): string | undefined {
    return this._location?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for location.
   * Use this to access openehr_base.String methods.
   */
  get $location(): openehr_base.String | undefined {
    return this._location;
  }

  /**
   * Sets location from either a primitive value or openehr_base.String wrapper.
   */
  set location(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._location = undefined;
    } else if (typeof val === "string") {
      this._location = openehr_base.String.from(val);
    } else {
      this._location = val;
    }
  }

  /**
   * The setting in which the clinical session took place. Coded using the openEHR Terminology,  setting  group.
   */
  setting?: DV_CODED_TEXT;
  /**
   * Other optional context which will be archetyped.
   */
  other_context?: ITEM_STRUCTURE;
  /**
   * The health care facility under whose care the event took place. This is the most specific workgroup or delivery unit within a care delivery enterprise that has an official identifier in the health system, and can be used to ensure medico-legal accountability.
   */
  health_care_facility?: PARTY_IDENTIFIED;
  /**
   * Parties involved in the healthcare event. These would normally include the physician(s) and often the patient (but not the latter if the clinical session is a pathology test for example).
   */
  participations?: undefined;
}
